/**
 * CanvasLayoutSelector Component
 *
 * Left panel that displays hardcoded canvas layout options (e.g. stacked 2, 2x2 grid).
 * Each option is a visual thumbnail showing the slot arrangement. Used in step 1 of the edit flow.
 */

import { getLayoutsForPaperType, getAspectRatioForLayout, SLOT_GAP_PERCENT } from '../types/canvas-layouts';
import type { CanvasLayout } from '../types/canvas-layouts';

export interface CanvasLayoutSelectorProps {
  /** Paper type: determines which layout options are shown */
  paperType: 'collage_4x6' | 'strip';
  /** ID of the layout selected for the current canvas */
  selectedLayoutId: string | null;
  /** Callback when a layout is selected */
  onLayoutSelect: (layoutId: string) => void;
}

/**
 * Renders an SVG thumbnail of the layout's slot arrangement (simple rectangles).
 * ViewBox matches paper aspect ratio so the SVG scales uniformly and slots do not overlap.
 * Slot coordinates (0-100) are mapped into the viewBox (portrait 100x150, landscape 150x100, strip 100x300).
 */
function LayoutThumbnail({ layout }: { layout: CanvasLayout }) {
  const half = SLOT_GAP_PERCENT / 2;
  const isPortrait4x6 = layout.paperType === 'collage_4x6' && layout.orientation === 'portrait';
  const isLandscape4x6 = layout.paperType === 'collage_4x6' && layout.orientation === 'landscape';
  const isStrip = layout.paperType === 'strip';

  const viewBox = isPortrait4x6
    ? '0 0 100 150'
    : isLandscape4x6
      ? '0 0 150 100'
      : isStrip
        ? '0 0 100 300'
        : '0 0 100 100';

  return (
    <svg
      viewBox={viewBox}
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      {layout.slots.map((slot, i) => {
        const x = slot.x + half;
        const y = slot.y + half;
        const w = Math.max(0, slot.width - SLOT_GAP_PERCENT);
        const h = Math.max(0, slot.height - SLOT_GAP_PERCENT);
        if (isPortrait4x6) {
          return (
            <rect
              key={i}
              x={x}
              y={y * 1.5}
              width={w}
              height={h * 1.5}
              fill="#e5e7eb"
              stroke="#9ca3af"
              strokeWidth={1}
            />
          );
        }
        if (isLandscape4x6) {
          return (
            <rect
              key={i}
              x={x * 1.5}
              y={y}
              width={w * 1.5}
              height={h}
              fill="#e5e7eb"
              stroke="#9ca3af"
              strokeWidth={1}
            />
          );
        }
        if (isStrip) {
          return (
            <rect
              key={i}
              x={x}
              y={y * 3}
              width={w}
              height={h * 3}
              fill="#e5e7eb"
              stroke="#9ca3af"
              strokeWidth={1}
            />
          );
        }
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={w}
            height={h}
            fill="#e5e7eb"
            stroke="#9ca3af"
            strokeWidth={1}
          />
        );
      })}
    </svg>
  );
}

/**
 * CanvasLayoutSelector - Left panel displaying canvas layout pattern options
 */
export function CanvasLayoutSelector({
  paperType,
  selectedLayoutId,
  onLayoutSelect,
}: CanvasLayoutSelectorProps) {
  const layouts = getLayoutsForPaperType(paperType);

  return (
    <div
      className="flex flex-col h-full min-h-0 bg-gray-50 rounded-lg shadow-md p-4"
      data-testid="canvas-layout-selector"
    >
      <div className="flex-shrink-0 mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Photo Layouts
        </h2>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4"
        data-testid="canvas-layout-list"
      >
        {(() => {
          const rows: CanvasLayout[][] = [];
          for (let i = 0; i < layouts.length; i += 2) {
            rows.push(layouts.slice(i, i + 2));
          }
          return rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="flex gap-4 flex-shrink-0 items-start"
            >
              {row.map((layout) => {
                const isSelected = layout.id === selectedLayoutId;
                return (
                  <button
                    key={layout.id}
                    type="button"
                    onClick={() => onLayoutSelect(layout.id)}
                    className={`
                      flex-1 min-w-0 rounded-lg overflow-hidden transition-all duration-200
                      border-4 text-left focus:outline-none focus:ring-4 focus:ring-yellow-500 focus:ring-offset-2
                      [&[data-selected=true]]:focus:ring-0 [&[data-selected=true]]:focus:ring-offset-0
                      ${isSelected
                        ? 'border-yellow-500 bg-yellow-50 shadow-lg'
                        : 'border-transparent hover:border-yellow-400 hover:shadow-md bg-white'
                      }
                    `}
                    data-testid={`canvas-layout-${layout.id}`}
                    data-selected={isSelected}
                    aria-pressed={isSelected}
                    aria-label={`${layout.name}, ${layout.slots.length} photo${layout.slots.length !== 1 ? 's' : ''}${isSelected ? ', selected' : ''}`}
                  >
                    <div
                      className="relative block w-full bg-gray-100"
                      style={{ aspectRatio: getAspectRatioForLayout(layout) }}
                    >
                      <LayoutThumbnail layout={layout} />
                      {isSelected && (
                        <div
                          className="absolute inset-0 bg-yellow-400 bg-opacity-20 pointer-events-none flex items-center justify-center"
                          aria-hidden
                        >
                          <div className="bg-yellow-500 text-white rounded-full p-1.5 shadow-lg">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              {row.length === 1 && <div className="flex-1 min-w-0" aria-hidden />}
            </div>
          ));
        })()}
      </div>

    </div>
  );
}
